import { List, useTable } from '@refinedev/antd';
import { Table, Space, Tag, Image, Button, Modal, Input, message } from 'antd';
import { CheckOutlined, CloseOutlined, ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';

interface Document {
  id: number;
  userId: number;
  type: string;
  fileUrl: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectedNote: string | null;
  expiresAt: string | null;
  createdAt: string;
  user: {
    fullName: string;
    phone: string;
  };
}

const documentTypeLabels: Record<string, string> = {
  DRIVER_LICENSE_FRONT: t('document.driverLicense'),
  DRIVER_SELFIE: t('document.selfie'),
  VEHICLE_REGISTRATION: t('document.techPassport'),
  VEHICLE_REGISTRATION_BACK: t('document.techPassportBack'),
  VEHICLE_PHOTO_FRONT: t('document.carFront'),
  VEHICLE_PHOTO_BACK: t('document.carBack'),
  VEHICLE_PHOTO_LEFT: t('document.carLeft'),
  VEHICLE_PHOTO_RIGHT: t('document.carRight'),
  VEHICLE_PHOTO_INTERIOR: t('document.carInterior'),
  VEHICLE_PHOTO_TRUNK: t('document.carTrunk'),
  VEHICLE_PHOTO_BACK_SEATS: t('document.carBackSeats'),
  VEHICLE_PHOTO_FRONT_SEAT: t('document.carFrontSeat'),
};

export const DocumentUserDocuments = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { can } = usePermission();
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const { tableProps, tableQueryResult } = useTable<Document>({
    resource: 'admin/documents',
    syncWithLocation: false,
    filters: {
      permanent: [{ field: 'userId', operator: 'eq', value: userId }],
    },
    pagination: {
      pageSize: 50,
    },
  });

  const documents = tableQueryResult.data?.data;
  const userName = documents && documents.length > 0 ? documents[0].user?.fullName || '' : '';
  const userPhone = documents && documents.length > 0 ? documents[0].user?.phone || '' : '';

  const handleApprove = async (doc: Document) => {
    try {
      await apiClient.put(`/admin/documents/${doc.id}/review`, {
        status: 'APPROVED',
      });
      message.success(t('documents.approveSuccess'));
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  // Overall driver-verification reject: rejects ALL of the driver's documents
  // with one shared note via /admin/drivers/:id/reject. Replaces the per-doc
  // reject flow so the driver gets a single, unambiguous rejection message
  // and admin sees a clean "needs re-review" state once the driver re-uploads.
  const handleRejectDriver = async () => {
    if (!userId) return;
    if (!rejectNote.trim()) {
      message.error(t('common.reason'));
      return;
    }
    try {
      await apiClient.post(`/admin/drivers/${userId}/reject`, {
        reason: rejectNote.trim(),
      });
      message.success(t('documents.rejectSuccess'));
      setRejectModalVisible(false);
      setRejectNote('');
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleBulkApprove = async () => {
    setBulkLoading(true);
    try {
      await Promise.all(
        selectedRowKeys.map((id) =>
          apiClient.put(`/admin/documents/${id}/review`, { status: 'APPROVED' }),
        ),
      );
      message.success(`${selectedRowKeys.length} ta hujjat tasdiqlandi`);
      setSelectedRowKeys([]);
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setBulkLoading(false);
    }
  };

  const columns = [
    {
      title: t('documents.image'),
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      width: 100,
      render: (url: string) => (
        <Image
          src={url}
          alt="Document"
          width={80}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    {
      title: t('documents.documentType'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => documentTypeLabels[type] || type,
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag
          color={
            status === 'APPROVED' ? 'green' : status === 'REJECTED' ? 'red' : 'orange'
          }
        >
          {status === 'APPROVED'
            ? t('documents.approved')
            : status === 'REJECTED'
              ? t('documents.rejected')
              : t('documents.pending')}
        </Tag>
      ),
    },
    {
      title: t('documents.expiry'),
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date: string | null) => {
        if (!date) return <Tag>{t('documents.notSet')}</Tag>;
        const expiresAt = new Date(date);
        const now = new Date();
        const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (daysLeft < 0) {
          return <Tag color="red">{t('documents.expired')}</Tag>;
        }
        if (daysLeft <= 30) {
          return (
            <Tag color="orange">
              {t('documents.expiringSoon')} ({expiresAt.toLocaleDateString('uz-UZ')})
            </Tag>
          );
        }
        return expiresAt.toLocaleDateString('uz-UZ');
      },
    },
    {
      title: t('common.date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: Document) =>
        can('documents', 'review') && record.status === 'PENDING' && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            size="small"
            onClick={() => handleApprove(record)}
          >
            Tasdiqlash
          </Button>
        ),
    },
  ];

  const title = userName
    ? `${userName} — ${userPhone}`
    : `Foydalanuvchi #${userId} hujjatlari`;

  return (
    <List
      title={title}
      headerButtons={
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/documents')}
          >
            Orqaga
          </Button>
          {can('documents', 'review') && (
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => setRejectModalVisible(true)}
            >
              Haydovchini rad etish
            </Button>
          )}
          <Button
            type="primary"
            icon={<UserOutlined />}
            onClick={() => navigate(`/drivers/${userId}`)}
          >
            Haydovchi profilini ko'rish
          </Button>
        </Space>
      }
    >
      {can('documents', 'review') && selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 16 }}>
          <span>{selectedRowKeys.length} {t('documents.selected')}</span>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={bulkLoading}
            onClick={handleBulkApprove}
          >
            Barchasini tasdiqlash
          </Button>
        </Space>
      )}
      <Table
        {...tableProps}
        dataSource={(tableProps.dataSource ?? []).filter(
          (d: Document) => d.type !== 'DRIVER_LICENSE_BACK',
        )}
        columns={columns}
        rowKey="id"
        pagination={false}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          getCheckboxProps: (record: Document) => ({
            disabled: record.status !== 'PENDING',
          }),
        }}
      />

      <Modal
        title={t('common.reason')}
        open={rejectModalVisible}
        onOk={handleRejectDriver}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectNote('');
        }}
        okText={t('common.reject')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, disabled: !rejectNote.trim() }}
      >
        <Input.TextArea
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          placeholder="Rad etish sababini kiriting..."
          rows={4}
        />
      </Modal>
    </List>
  );
};
